import React, { ChangeEvent, SyntheticEvent, useState, useEffect, useRef } from "react";
import { useModal } from "../../store/modal/hooks";
import { FloppyDiskIcon } from "../common/Icons";
import { Form, Input, Label, Required } from "../common/Input";
import { Spacer } from "../common/Spacer";
import { StandardModal } from "./StandardModal";
import { useTranslation } from "react-i18next";

type SaveConfigurationProps = {
    onSave: (name: string, projectId?: string) => void;
    initialName?: string;
    initialProjectId?: string;
    isEditing?: boolean;
};

// Mirrors the backend sanitizeProjectId rules: the id must follow
// [countrycode letters]_[year numbers]_[project number] (e.g. "BE_2026_1").
const PROJECT_ID_PATTERN = /^[A-Za-z]+_[0-9]+_[0-9]+$/;

export const SaveConfiguration = ({ onSave, initialName = "", initialProjectId = "", isEditing = false }: SaveConfigurationProps) => {
    const { t } = useTranslation() as any;
    const { closeModal } = useModal();
    const [name, setName] = useState(initialName);
    const [nameError, setNameError] = useState("");
    const [projectId, setProjectId] = useState(initialProjectId);
    const [projectIdError, setProjectIdError] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    function handleNameChange(e: ChangeEvent<HTMLInputElement>) {
        setName(e.target.value);
        if (nameError) {
            setNameError("");
        }
    }

    function handleProjectIdChange(e: ChangeEvent<HTMLInputElement>) {
        setProjectId(e.target.value);
        if (projectIdError) {
            setProjectIdError("");
        }
    }

    function handleSubmit(e: SyntheticEvent) {
        e.preventDefault();

        if (!name.trim()) {
            setNameError(t("account.configurationModal.nameInputError"));
            return;
        }

        const trimmedProjectId = projectId.trim();
        if (isEditing && trimmedProjectId && !PROJECT_ID_PATTERN.test(trimmedProjectId)) {
            setProjectIdError(
                t("account.configurationModal.projectIdInputError") ||
                "Use the format COUNTRY_YEAR_NUMBER (e.g. BE_2026_1)."
            );
            return;
        }

        onSave(name.trim(), isEditing ? trimmedProjectId : undefined);
        closeModal();
    }

    return (
        <StandardModal
            titleIcon={FloppyDiskIcon}
            title={isEditing ? t("account.configurationModal.modalEditTitle") : t("account.configurationModal.modalTitle")}
            subtitle={isEditing ? t("account.configurationModal.modalEditText") : t("account.configurationModal.modalText")}
            buttonText={isEditing ? t("account.configurationModal.modalEditTitle") : t("account.configurationModal.modalTitle")}
            handleSubmit={handleSubmit}
            classnames="save-configuration"
        >
            <Spacer heightPx={24} />
            <Form onSubmit={handleSubmit}>
                <Label htmlFor="configuration-name">
                    {t("account.configurationModal.nameInputTitle")} <Required>*</Required>
                </Label>
                <Input
                    ref={inputRef}
                    id="configuration-name"
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    placeholder={t("account.configurationModal.nameInputPlaceholder")}
                    invalid={!!nameError}
                    maxLength={100}
                />
                {nameError && <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{nameError}</p>}

                {isEditing && (
                    <>
                        <Spacer heightPx={16} />
                        <Label htmlFor="configuration-project-id">
                            {t("account.configurationModal.projectIdInputTitle") || "Project ID"}
                        </Label>
                        <Input
                            id="configuration-project-id"
                            type="text"
                            value={projectId}
                            onChange={handleProjectIdChange}
                            placeholder={t("account.configurationModal.projectIdInputPlaceholder") || "Project ID"}
                            invalid={!!projectIdError}
                            maxLength={100}
                        />
                        {projectIdError && <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{projectIdError}</p>}
                    </>
                )}
            </Form>
        </StandardModal>
    );
};
